import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, InlineStack, Spinner, Text } from '@shopify/polaris';

const stepRuntimePromise = Promise.all([import('three'), import('three/examples/jsm/controls/OrbitControls.js')]).then(async ([threeModule, controlsModule]) => {
  const THREE = threeModule;
  const { OrbitControls } = controlsModule;
  return { THREE, OrbitControls };
});

const getThreeColor = (THREE, color = []) => {
  const [r = 190, g = 190, b = 190] = color;
  const normalize = (value) => (value > 1 ? value / 255 : value);
  return new THREE.Color(normalize(r), normalize(g), normalize(b));
};

const disposeGroup = (group) => {
  if (!group) return;

  group.traverse((child) => {
    if (child.geometry) child.geometry.dispose();

    if (Array.isArray(child.material)) {
      child.material.forEach((material) => material.dispose());
    } else if (child.material) {
      child.material.dispose();
    }
  });
};

const fitCameraToObject = (THREE, camera, controls, object) => {
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDimension = Math.max(size.x, size.y, size.z) || 1;
  const fov = (camera.fov * Math.PI) / 180;
  const distance = maxDimension / 2 / Math.tan(fov / 2);

  camera.near = Math.max(distance / 100, 0.1);
  camera.far = distance * 100;
  camera.position.set(center.x + distance * 1.3, center.y + distance, center.z + distance * 1.3);
  camera.lookAt(center);
  camera.updateProjectionMatrix();

  controls.target.copy(center);
  controls.minDistance = distance / 10;
  controls.maxDistance = distance * 10;
  controls.update();
};

const createPersonReference = (THREE) => {
  const personGroup = new THREE.Group();
  const clothesMaterial = new THREE.MeshStandardMaterial({
    color: '#4f46e5',
    roughness: 0.82,
    metalness: 0.04
  });
  const skinMaterial = new THREE.MeshStandardMaterial({
    color: '#f1c27d',
    roughness: 0.95,
    metalness: 0.01
  });
  const darkMaterial = new THREE.MeshStandardMaterial({
    color: '#334155',
    roughness: 0.9,
    metalness: 0.02
  });

  const head = new THREE.Mesh(new THREE.SphereGeometry(95, 24, 24), skinMaterial);
  head.position.set(0, 1705, 0);
  personGroup.add(head);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(34, 38, 45, 16), skinMaterial);
  neck.position.set(0, 1580, 0);
  personGroup.add(neck);

  const torso = new THREE.Mesh(new THREE.CylinderGeometry(160, 120, 520, 24), clothesMaterial);
  torso.position.set(0, 1280, 0);
  personGroup.add(torso);

  const pelvis = new THREE.Mesh(new THREE.CylinderGeometry(118, 135, 170, 20), darkMaterial);
  pelvis.position.set(0, 940, 0);
  personGroup.add(pelvis);

  const leftArm = new THREE.Mesh(new THREE.CylinderGeometry(34, 42, 520, 16), clothesMaterial);
  leftArm.position.set(-210, 1280, 0);
  leftArm.rotation.z = 0.12;
  personGroup.add(leftArm);

  const rightArm = new THREE.Mesh(new THREE.CylinderGeometry(34, 42, 520, 16), clothesMaterial);
  rightArm.position.set(210, 1280, 0);
  rightArm.rotation.z = -0.12;
  personGroup.add(rightArm);

  const leftHand = new THREE.Mesh(new THREE.SphereGeometry(34, 16, 16), skinMaterial);
  leftHand.position.set(-240, 1020, 0);
  personGroup.add(leftHand);

  const rightHand = new THREE.Mesh(new THREE.SphereGeometry(34, 16, 16), skinMaterial);
  rightHand.position.set(240, 1020, 0);
  personGroup.add(rightHand);

  const leftLeg = new THREE.Mesh(new THREE.CylinderGeometry(56, 48, 820, 18), darkMaterial);
  leftLeg.position.set(-72, 450, 0);
  personGroup.add(leftLeg);

  const rightLeg = new THREE.Mesh(new THREE.CylinderGeometry(56, 48, 820, 18), darkMaterial);
  rightLeg.position.set(72, 450, 0);
  personGroup.add(rightLeg);

  const leftFoot = new THREE.Mesh(new THREE.BoxGeometry(90, 45, 220), darkMaterial);
  leftFoot.position.set(-72, 18, 70);
  personGroup.add(leftFoot);

  const rightFoot = new THREE.Mesh(new THREE.BoxGeometry(90, 45, 220), darkMaterial);
  rightFoot.position.set(72, 18, 70);
  personGroup.add(rightFoot);

  const base = new THREE.Mesh(new THREE.CylinderGeometry(210, 210, 18, 32), new THREE.MeshStandardMaterial({ color: '#cbd5e1', roughness: 1, metalness: 0 }));
  base.position.set(0, -9, 0);
  personGroup.add(base);

  personGroup.userData.isReferencePerson = true;
  return personGroup;
};

const positionPersonReference = (THREE, modelGroup, personGroup) => {
  const modelBox = new THREE.Box3().setFromObject(modelGroup);
  const modelSize = modelBox.getSize(new THREE.Vector3());
  const modelCenter = modelBox.getCenter(new THREE.Vector3());
  const personBox = new THREE.Box3().setFromObject(personGroup);
  const personSize = personBox.getSize(new THREE.Vector3());
  const sideOffset = Math.max(modelSize.x * 0.14, 160);

  personGroup.position.set(modelBox.max.x + personSize.x / 2 + sideOffset, modelBox.min.y, modelCenter.z);
  personGroup.rotation.y = -0.28;
};

const formatDimension = (value) => {
  if (!Number.isFinite(value)) return '-';
  if (value >= 1000) return `${(value / 1000).toFixed(2)} m`;
  return `${Math.round(value)} mm`;
};

const LARGE_STEP_FILE_THRESHOLD_BYTES = 15 * 1024 * 1024;

const formatFileSize = (byteLength = 0) => {
  if (!Number.isFinite(byteLength) || byteLength <= 0) return '-';
  return `${(byteLength / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (milliseconds) => {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '-';
  const totalSeconds = Math.round(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

const sleep = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const getServerProgressPercent = (snapshot) => {
  if (!snapshot) return 8;

  const { phase, elapsedMs, estimatedTotalMs, status } = snapshot;
  if (status === 'completed') return 100;

  switch (phase) {
    case 'queued':
      return 8;
    case 'sharepoint-download:start':
      return 16;
    case 'sharepoint-download:done':
      return 28;
    case 'runtime-init:start':
      return 38;
    case 'runtime-init:done':
      return 48;
    case 'parse:start': {
      if (Number.isFinite(estimatedTotalMs) && estimatedTotalMs > 0) {
        const ratio = Math.min(elapsedMs / estimatedTotalMs, 1.15);
        return Math.min(88, Math.max(56, Math.round(56 + ratio * 28)));
      }
      return 64;
    }
    case 'parse:done':
      return 90;
    case 'building-scene':
      return 96;
    default:
      return 12;
  }
};

const formatLoadingPhase = (phase) => {
  const labels = {
    queued: 'Queued',
    'sharepoint-download:start': 'Downloading from SharePoint',
    'sharepoint-download:done': 'Download complete',
    'runtime-init:start': 'Preparing STEP engine',
    'runtime-init:done': 'STEP engine ready',
    'parse:start': 'Converting geometry',
    'parse:done': 'Conversion done',
    'building-scene': 'Building scene',
    completed: 'Complete'
  };

  if (!phase) return 'Starting';
  if (labels[phase]) return labels[phase];
  if (phase.startsWith('browser:')) return `Browser parse (${phase.replace('browser:', '')})`;
  return phase;
};

export default function StepModelPreview({ arrayBuffer, fileName, sharepointPath, showReferenceFigure = false }) {
  const mountRef = useRef(null);
  const runtimeRef = useRef(null);
  const workerRef = useRef(null);
  const previewRequestAbortRef = useRef(null);
  const activeServerJobIdRef = useRef(null);
  const requestIdRef = useRef(0);
  const resizeObserverRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const sceneRef = useRef(null);
  const controlsRef = useRef(null);
  const frameRef = useRef(null);
  const gridHelperRef = useRef(null);
  const modelGroupRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Rendering 3D preview...');
  const [error, setError] = useState(null);
  const [viewerReady, setViewerReady] = useState(false);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [previewQualityLabel, setPreviewQualityLabel] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [showGrid, setShowGrid] = useState(true);

  const fileSizeBytes = arrayBuffer?.byteLength || 0;
  const isLargeStepFile = fileSizeBytes >= LARGE_STEP_FILE_THRESHOLD_BYTES;
  const shouldUseServerPreview = isLargeStepFile && Boolean(sharepointPath);

  useEffect(() => {
    setPreviewQualityLabel(null);
    setModelDimensions(null);
    setLoadingDetails(null);
    activeServerJobIdRef.current = null;
    if (previewRequestAbortRef.current) {
      previewRequestAbortRef.current.abort();
      previewRequestAbortRef.current = null;
    }
  }, [arrayBuffer, fileName, sharepointPath]);

  useEffect(() => {
    if (gridHelperRef.current) {
      gridHelperRef.current.visible = showGrid;
    }
  }, [showGrid, viewerReady]);

  useEffect(() => {
    let cancelled = false;

    const initializeViewer = async () => {
      const runtime = await stepRuntimePromise;
      if (cancelled || !mountRef.current) return;

      runtimeRef.current = runtime;

      const { THREE, OrbitControls } = runtime;
      const container = mountRef.current;
      const scene = new THREE.Scene();
      scene.background = new THREE.Color('#f6f6f7');

      const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 5000);
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      renderer.setPixelRatio(window.devicePixelRatio || 1);
      renderer.shadowMap.enabled = false;
      container.appendChild(renderer.domElement);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.screenSpacePanning = true;

      scene.add(new THREE.AmbientLight('#ffffff', 1.7));

      const directionalLight = new THREE.DirectionalLight('#ffffff', 1.4);
      directionalLight.position.set(12, 10, 8);
      scene.add(directionalLight);

      const fillLight = new THREE.DirectionalLight('#ffffff', 0.8);
      fillLight.position.set(-10, 6, -8);
      scene.add(fillLight);

      const gridHelper = new THREE.GridHelper(2000, 20, '#c9cccf', '#e1e3e5');
      gridHelper.position.y = 0;
      scene.add(gridHelper);

      gridHelperRef.current = gridHelper;
      rendererRef.current = renderer;
      cameraRef.current = camera;
      sceneRef.current = scene;
      controlsRef.current = controls;

      const setSize = () => {
        if (!container || !renderer || !camera) return;
        const width = container.clientWidth || 1;
        const height = container.clientHeight || 1;
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      };

      setSize();
      resizeObserverRef.current = new ResizeObserver(setSize);
      resizeObserverRef.current.observe(container);

      const renderFrame = () => {
        frameRef.current = requestAnimationFrame(renderFrame);
        controls.update();
        renderer.render(scene, camera);
      };

      renderFrame();
      setViewerReady(true);
    };

    initializeViewer().catch((viewerError) => {
      if (!cancelled) {
        console.error('Failed to initialize STEP viewer:', viewerError);
        setError('The 3D viewer could not be initialized.');
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
      if (controlsRef.current) controlsRef.current.dispose();
      if (workerRef.current) workerRef.current.terminate();
      disposeGroup(modelGroupRef.current);
      if (rendererRef.current) rendererRef.current.dispose();
      if (rendererRef.current?.domElement?.parentNode === mountRef.current) {
        mountRef.current.removeChild(rendererRef.current.domElement);
      }
    };
  }, []);

  useEffect(() => {
    if (!arrayBuffer || !viewerReady || !sceneRef.current || !cameraRef.current || !controlsRef.current || !runtimeRef.current) return undefined;

    let cancelled = false;
    const abortController = new AbortController();
    previewRequestAbortRef.current = abortController;

    const loadModel = async () => {
      setLoading(true);
      setLoadingMessage(shouldUseServerPreview ? 'Generating 3D preview on server...' : 'Parsing STEP file...');
      setLoadingDetails({
        phase: shouldUseServerPreview ? 'queued' : 'local-parse',
        message: shouldUseServerPreview ? 'Sending the STEP file to the server preview pipeline...' : 'Parsing STEP file in the browser...',
        progressPercent: shouldUseServerPreview ? 8 : 20,
        elapsedMs: 0,
        estimatedRemainingMs: null,
        isEstimate: false,
        fileSizeBytes,
        previewProfile: null
      });
      setError(null);

      try {
        const { THREE } = runtimeRef.current;
        let result = null;
        if (shouldUseServerPreview) {
          const response = await fetch('/api/docgen/sharepoint-step-preview-job', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: sharepointPath, fileName }),
            signal: abortController.signal
          });

          let payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(payload?.msg || 'Server preview generation failed.');
          }

          const applyServerSnapshot = (snapshot) => {
            setLoadingDetails({
              phase: snapshot.phase,
              message: snapshot.message || 'Generating STEP preview on the server...',
              progressPercent: getServerProgressPercent(snapshot),
              elapsedMs: snapshot.elapsedMs ?? 0,
              estimatedRemainingMs: snapshot.estimatedRemainingMs,
              isEstimate: Boolean(snapshot.isEstimate),
              fileSizeBytes: snapshot.fileSizeBytes ?? fileSizeBytes,
              previewProfile: snapshot.previewProfile ?? null
            });
          };

          applyServerSnapshot(payload);
          activeServerJobIdRef.current = payload.jobId || null;

          while (!cancelled && payload.status !== 'completed' && payload.status !== 'failed') {
            await sleep(1800);
            if (cancelled) return;

            const statusResponse = await fetch('/api/docgen/sharepoint-step-preview-job-status', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ jobId: payload.jobId }),
              signal: abortController.signal
            });

            const statusPayload = await statusResponse.json().catch(() => ({}));
            if (!statusResponse.ok) {
              throw new Error(statusPayload?.msg || 'Could not read STEP preview status.');
            }

            payload = statusPayload;
            applyServerSnapshot(payload);
          }

          if (payload.status === 'failed') {
            throw new Error(payload.error || payload.message || 'Server preview generation failed.');
          }

          result = payload.payload;
          setLoadingDetails((current) => ({
            ...(current || {}),
            phase: 'building-scene',
            message: 'Server conversion is done. Building the 3D scene in your browser...',
            progressPercent: 96,
            elapsedMs: payload.elapsedMs ?? current?.elapsedMs ?? 0,
            estimatedRemainingMs: null,
            isEstimate: false,
            previewProfile: payload.previewProfile ?? current?.previewProfile ?? null
          }));
        } else {
          const sizeInMb = arrayBuffer.byteLength / (1024 * 1024);
          const parseAttempts =
            sizeInMb <= 5
              ? [{ profileLabel: 'standard', timeoutMs: 12000 }]
              : sizeInMb <= 12
              ? [
                  { profileLabel: 'balanced', timeoutMs: 12000 },
                  { profileLabel: 'fast', timeoutMs: 15000 },
                  { profileLabel: 'ultra-fast', timeoutMs: 18000 }
                ]
              : sizeInMb <= 25
              ? [
                  { profileLabel: 'fast', timeoutMs: 12000 },
                  { profileLabel: 'ultra-fast', timeoutMs: 16000 },
                  { profileLabel: 'assembly-fast', timeoutMs: 18000 },
                  { profileLabel: 'assembly-ultra-fast', timeoutMs: 22000 }
                ]
              : [
                  { profileLabel: 'ultra-fast', timeoutMs: 15000 },
                  { profileLabel: 'assembly-fast', timeoutMs: 18000 },
                  { profileLabel: 'assembly-ultra-fast', timeoutMs: 22000 }
                ];

          let lastAttemptError = null;

          for (const attempt of parseAttempts) {
            setLoadingMessage(`Parsing STEP file... (${attempt.profileLabel})`);
            setLoadingDetails({
              phase: `browser:${attempt.profileLabel}`,
              message: `Parsing STEP file in the browser with the ${attempt.profileLabel} profile...`,
              progressPercent: 28,
              elapsedMs: 0,
              estimatedRemainingMs: null,
              isEstimate: false,
              fileSizeBytes,
              previewProfile: attempt.profileLabel
            });

            try {
              result = await new Promise((resolve, reject) => {
                const worker = new Worker(new URL('./stepImport.worker.js', import.meta.url), { type: 'module' });
                workerRef.current = worker;

                const requestId = requestIdRef.current + 1;
                requestIdRef.current = requestId;
                const timeoutId = window.setTimeout(() => {
                  worker.terminate();
                  if (workerRef.current === worker) workerRef.current = null;
                  reject(new Error(`timeout:${attempt.profileLabel}`));
                }, attempt.timeoutMs);

                worker.onmessage = (event) => {
                  const { id, result, error, previewProfile } = event.data || {};
                  if (id !== requestId) return;
                  window.clearTimeout(timeoutId);
                  worker.terminate();
                  if (workerRef.current === worker) workerRef.current = null;
                  if (error) reject(new Error(error));
                  else resolve({ result, previewProfile });
                };

                worker.onerror = () => {
                  window.clearTimeout(timeoutId);
                  worker.terminate();
                  if (workerRef.current === worker) workerRef.current = null;
                  reject(new Error(`worker-crash:${attempt.profileLabel}`));
                };

                const bufferCopy = arrayBuffer.slice(0);
                worker.postMessage({ id: requestId, profileLabel: attempt.profileLabel, arrayBuffer: bufferCopy }, [bufferCopy]);
              });

              lastAttemptError = null;
              break;
            } catch (attemptError) {
              lastAttemptError = attemptError;
              if (cancelled) return;
            }
          }

          if (!result) {
            throw lastAttemptError || new Error('STEP parsing did not complete.');
          }
        }

        if (cancelled) return;

        if (result.previewProfile && result.previewProfile !== 'standard') {
          setPreviewQualityLabel(result.previewProfile);
        } else {
          setPreviewQualityLabel(null);
        }
        setLoadingMessage('Building 3D mesh...');
        setLoadingDetails((current) => ({
          ...(current || {}),
          phase: 'building-scene',
          message: 'Building the 3D scene in your browser...',
          progressPercent: 96,
          estimatedRemainingMs: null,
          isEstimate: false
        }));

        if (!result?.result?.success || !Array.isArray(result.result.meshes) || result.result.meshes.length === 0) {
          throw new Error('No mesh data returned');
        }

        const group = new THREE.Group();

        result.result.meshes.forEach((meshData, meshIndex) => {
          if (!meshData?.attributes?.position?.array || !meshData?.index?.array) return;

          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute('position', new THREE.Float32BufferAttribute(meshData.attributes.position.array, 3));

          if (meshData.attributes.normal?.array) {
            geometry.setAttribute('normal', new THREE.Float32BufferAttribute(meshData.attributes.normal.array, 3));
          } else {
            geometry.computeVertexNormals();
          }

          geometry.setIndex(meshData.index.array);

          const material = new THREE.MeshStandardMaterial({
            color: getThreeColor(THREE, meshData.color),
            metalness: 0.12,
            roughness: 0.68,
            side: THREE.DoubleSide
          });

          const mesh = new THREE.Mesh(geometry, material);
          mesh.name = meshData.name || `mesh-${meshIndex}`;
          group.add(mesh);
        });

        const modelBox = new THREE.Box3().setFromObject(group);
        const modelSize = modelBox.getSize(new THREE.Vector3());
        const modelCenter = modelBox.getCenter(new THREE.Vector3());

        // Normalize every imported model onto a predictable preview floor.
        group.position.x -= modelCenter.x;
        group.position.y -= modelBox.min.y;
        group.position.z -= modelCenter.z;

        const normalizedModelBox = new THREE.Box3().setFromObject(group);
        const normalizedModelSize = normalizedModelBox.getSize(new THREE.Vector3());
        setModelDimensions({
          width: normalizedModelSize.x,
          height: normalizedModelSize.y,
          depth: normalizedModelSize.z
        });

        if (gridHelperRef.current) {
          const maxFootprint = Math.max(normalizedModelSize.x, normalizedModelSize.z, 1000);
          const targetGridSize = Math.max(2000, Math.ceil((maxFootprint * 1.6) / 500) * 500);
          const targetGridDivisions = Math.max(20, Math.round(targetGridSize / 100));

          sceneRef.current.remove(gridHelperRef.current);
          gridHelperRef.current.geometry.dispose();
          if (Array.isArray(gridHelperRef.current.material)) {
            gridHelperRef.current.material.forEach((material) => material.dispose());
          } else {
            gridHelperRef.current.material.dispose();
          }

          const resizedGridHelper = new THREE.GridHelper(targetGridSize, targetGridDivisions, '#c9cccf', '#e1e3e5');
          resizedGridHelper.position.y = 0;
          resizedGridHelper.visible = showGrid;
          sceneRef.current.add(resizedGridHelper);
          gridHelperRef.current = resizedGridHelper;
        }

        if (showReferenceFigure) {
          const personGroup = createPersonReference(THREE);
          positionPersonReference(THREE, group, personGroup);
          group.add(personGroup);
        }

        if (cancelled) {
          disposeGroup(group);
          return;
        }

        if (modelGroupRef.current) {
          sceneRef.current.remove(modelGroupRef.current);
          disposeGroup(modelGroupRef.current);
        }

        modelGroupRef.current = group;
        sceneRef.current.add(group);
        fitCameraToObject(THREE, cameraRef.current, controlsRef.current, group);
        setLoadingDetails((current) => ({
          ...(current || {}),
          phase: 'completed',
          message: '3D preview ready.',
          progressPercent: 100,
          estimatedRemainingMs: 0,
          isEstimate: false
        }));
      } catch (loadError) {
        if (!cancelled) {
          if (loadError?.name === 'AbortError') {
            return;
          }
          console.error('Failed to render STEP file:', loadError);
          setError(shouldUseServerPreview ? 'The server could not generate a 3D preview for this STEP file.' : 'This STEP file could not be rendered as a 3D model.');
          setModelDimensions(null);
          setPreviewQualityLabel(null);
        }
      } finally {
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }

        if (!cancelled) {
          setLoading(false);
          setLoadingMessage('Rendering 3D preview...');
          setLoadingDetails(null);
        }
      }
    };

    loadModel();

    return () => {
      cancelled = true;
      abortController.abort();
      if (previewRequestAbortRef.current === abortController) {
        previewRequestAbortRef.current = null;
      }
      const jobIdToCancel = activeServerJobIdRef.current;
      activeServerJobIdRef.current = null;
      if (shouldUseServerPreview && jobIdToCancel) {
        fetch('/api/docgen/sharepoint-step-preview-job-cancel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId: jobIdToCancel })
        }).catch(() => {});
      }
    };
  }, [arrayBuffer, fileName, fileSizeBytes, sharepointPath, shouldUseServerPreview, showGrid, showReferenceFigure, viewerReady]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '70vh', borderRadius: '8px', overflow: 'hidden', background: '#f6f6f7' }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />

      {modelDimensions ? (
        <div
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            padding: '10px 12px',
            borderRadius: '10px',
            background: 'rgba(255, 255, 255, 0.94)',
            boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
            border: '1px solid rgba(148, 163, 184, 0.25)',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            zIndex: 2
          }}
        >
          <Text variant="bodySm" fontWeight="semibold">
            Model dimensions
          </Text>
          <Text variant="bodySm" tone="subdued">
            W {formatDimension(modelDimensions.width)} | H {formatDimension(modelDimensions.height)} | D {formatDimension(modelDimensions.depth)}
          </Text>
          {previewQualityLabel ? (
            <Text variant="bodySm" tone="subdued">
              Preview quality: {previewQualityLabel}
            </Text>
          ) : null}
        </div>
      ) : null}

      {viewerReady ? (
        <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 2 }}>
          <Button size="slim" onClick={() => setShowGrid((current) => !current)}>
            {showGrid ? 'Hide grid' : 'Show grid'}
          </Button>
        </div>
      ) : null}

      {loading ? (
        <Box padding="400" style={{ position: 'absolute', inset: 0, background: 'rgba(246, 246, 247, 0.8)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div
              style={{
                width: 'min(560px, 100%)',
                background: 'rgba(255, 255, 255, 0.96)',
                border: '1px solid rgba(148, 163, 184, 0.25)',
                borderRadius: '16px',
                boxShadow: '0 18px 36px rgba(15, 23, 42, 0.12)',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <InlineStack gap="300" blockAlign="center">
                  <Spinner size="large" accessibilityLabel="Loading 3D preview" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Text variant="headingMd" as="h3">
                      Preparing 3D preview
                    </Text>
                    <Text tone="subdued">{loadingDetails?.message || loadingMessage}</Text>
                  </div>
                </InlineStack>

                <div
                  style={{
                    width: '100%',
                    height: '10px',
                    borderRadius: '999px',
                    background: 'rgba(203, 213, 225, 0.55)',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      width: `${loadingDetails?.progressPercent || 12}%`,
                      height: '100%',
                      borderRadius: '999px',
                      background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
                      transition: 'width 300ms ease'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 18px' }}>
                  <Text variant="bodySm" tone="subdued">
                    Stage: {formatLoadingPhase(loadingDetails?.phase)}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    Elapsed: {formatDuration(loadingDetails?.elapsedMs)}
                  </Text>
                  {loadingDetails?.estimatedRemainingMs != null ? (
                    <Text variant="bodySm" tone="subdued">
                      {loadingDetails?.isEstimate ? 'Est. remaining:' : 'Remaining:'} {formatDuration(loadingDetails?.estimatedRemainingMs)}
                    </Text>
                  ) : null}
                  <Text variant="bodySm" tone="subdued">
                    File size: {formatFileSize(loadingDetails?.fileSizeBytes || fileSizeBytes)}
                  </Text>
                  {loadingDetails?.previewProfile ? (
                    <Text variant="bodySm" tone="subdued">
                      Profile: {loadingDetails.previewProfile}
                    </Text>
                  ) : null}
                </div>

                {shouldUseServerPreview ? (
                  <Text variant="bodySm" tone="subdued">
                    Large STEP previews are converted on the server first. The remaining time is an estimate because the STEP parser does not expose precise percentage progress.
                  </Text>
                ) : null}
              </div>
            </div>
          </div>
        </Box>
      ) : null}

      {error ? (
        <Box padding="400" style={{ position: 'absolute', inset: 0, background: 'rgba(246, 246, 247, 0.92)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', maxWidth: '560px' }}>
              <Text tone="critical">{error}</Text>
              {isLargeStepFile ? (
                <Text tone="subdued">
                  {shouldUseServerPreview
                    ? `The file is ${formatFileSize(fileSizeBytes)}. Large STEP previews now use a server-generated mesh, but this assembly still failed during preview generation.`
                    : 'This assembly is large enough that in-browser STEP parsing may fail even with a coarse preview.'}
                </Text>
              ) : null}
            </div>
          </div>
        </Box>
      ) : null}
    </div>
  );
}
